import { useCallback, useEffect, useRef } from 'react';
import { driver, Driver, DriveStep } from 'driver.js';
import 'driver.js/dist/driver.css';

/**
 * Tour State Management
 * Tracks which tours have been completed per user
 */
export interface TourState {
  wallet_generic_completed: boolean;
  wallet_coupon_completed: boolean;
  wallet_event_completed: boolean;
  wallet_storecard_completed: boolean;
  signature_completed: boolean;
  card_completed: boolean;
  lastDismissed?: string; // ISO date if user clicked "Remind me later"
}

const TOUR_STORAGE_KEY = 'guidedTours';

/**
 * Get tour state from localStorage
 */
export const getTourState = (): TourState => {
  try {
    const stored = localStorage.getItem(TOUR_STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (e) {
    console.error('[Tour] Failed to parse tour state:', e);
  }
  return {
    wallet_generic_completed: false,
    wallet_coupon_completed: false,
    wallet_event_completed: false,
    wallet_storecard_completed: false,
    signature_completed: false,
    card_completed: false,
  };
};

/**
 * Save tour state to localStorage
 */
export const saveTourState = (state: Partial<TourState>) => {
  try {
    const current = getTourState();
    const updated = { ...current, ...state };
    localStorage.setItem(TOUR_STORAGE_KEY, JSON.stringify(updated));
  } catch (e) {
    console.error('[Tour] Failed to save tour state:', e);
  }
};

/**
 * Check if a specific tour has been completed
 */
export const isTourCompleted = (tourKey: keyof TourState): boolean => {
  const state = getTourState();
  return state[tourKey] === true;
};

/**
 * Mark a tour as completed
 */
export const markTourCompleted = (tourKey: keyof TourState) => {
  saveTourState({ [tourKey]: true });
};

/**
 * Reset a specific tour (allow replay)
 */
export const resetTour = (tourKey: keyof TourState) => {
  saveTourState({ [tourKey]: false });
};

/**
 * Reset all tours
 */
export const resetAllTours = () => {
  localStorage.removeItem(TOUR_STORAGE_KEY);
};

/**
 * Tour Configurations
 */
export type TourType = 
  | 'wallet_generic' 
  | 'wallet_coupon' 
  | 'wallet_event' 
  | 'wallet_storecard'
  | 'signature'
  | 'card';

interface TourConfig {
  steps: DriveStep[];
  onComplete?: () => void;
  onSkip?: () => void;
}

/**
 * Wallet Generic Tour Steps (Digital Business Card)
 * 
 * The wallet editor has 4 tabs: Template, Front of Pass, Back of Pass, Features
 * This tour introduces the structure and key areas.
 */
const walletGenericSteps: DriveStep[] = [
  {
    element: '#tab-template',
    popover: {
      title: '📋 Step 1: Choose Template',
      description: 'Start here! Select "Business Card" for a Digital Business Card. Other templates are available for companies.',
      side: 'bottom',
      align: 'start',
    },
  },
  {
    element: '#tab-front',
    popover: {
      title: '✏️ Step 2: Front of Pass',
      description: 'Click here to customize your pass name, logo, and colors. This is where you design how your card looks.',
      side: 'bottom',
      align: 'center',
    },
  },
  {
    element: '#tab-back',
    popover: {
      title: '📝 Step 3: Back of Pass',
      description: 'Add extra info like website, address, or custom fields. Users see this when they flip the card.',
      side: 'bottom',
      align: 'center',
    },
  },
  {
    element: '#tab-features',
    popover: {
      title: '⚙️ Step 4: Features',
      description: 'Enable QR codes, notifications, and other advanced features for your pass.',
      side: 'bottom',
      align: 'end',
    },
  },
  {
    element: '[data-tour="wallet-preview"]',
    popover: {
      title: '📱 Real-Time Preview',
      description: 'See exactly how your pass will look on Apple or Google Wallet. Toggle between them to compare!',
      side: 'left',
      align: 'center',
    },
  },
  {
    element: '[data-tour="add-to-wallet"]',
    popover: {
      title: '🎉 Add to Your Phone!',
      description: 'When you\'re happy with your design, click here to add it to Apple Wallet or Google Wallet.',
      side: 'top',
      align: 'center',
    },
  },
];

/**
 * Get tour configuration by type
 */
export const getTourConfig = (tourType: TourType): TourConfig => {
  switch (tourType) {
    case 'wallet_generic':
      return {
        steps: walletGenericSteps,
        onComplete: () => markTourCompleted('wallet_generic_completed'),
        onSkip: () => markTourCompleted('wallet_generic_completed'),
      };
    case 'wallet_coupon':
      return {
        steps: walletGenericSteps, // TODO: Create coupon-specific steps
        onComplete: () => markTourCompleted('wallet_coupon_completed'),
        onSkip: () => markTourCompleted('wallet_coupon_completed'),
      };
    case 'wallet_event':
      return {
        steps: walletGenericSteps, // TODO: Create event-specific steps
        onComplete: () => markTourCompleted('wallet_event_completed'),
        onSkip: () => markTourCompleted('wallet_event_completed'),
      };
    case 'wallet_storecard':
      return {
        steps: walletGenericSteps, // TODO: Create storecard-specific steps
        onComplete: () => markTourCompleted('wallet_storecard_completed'),
        onSkip: () => markTourCompleted('wallet_storecard_completed'),
      };
    case 'signature':
      return {
        steps: [], // TODO: Create signature tour steps
        onComplete: () => markTourCompleted('signature_completed'),
        onSkip: () => markTourCompleted('signature_completed'),
      };
    case 'card':
      return {
        steps: [], // TODO: Create card tour steps
        onComplete: () => markTourCompleted('card_completed'),
        onSkip: () => markTourCompleted('card_completed'),
      };
    default:
      return { steps: [] };
  }
};

/**
 * Custom driver.js theme/styling to match brand
 */
const driverConfig = {
  showProgress: true,
  animate: true,
  smoothScroll: true,
  allowClose: true,
  overlayColor: 'rgba(0, 0, 0, 0.7)',
  stagePadding: 10,
  stageRadius: 8,
  popoverClass: 'sf-tour-popover',
  progressText: 'Step {{current}} of {{total}}',
  nextBtnText: 'Next →',
  prevBtnText: '← Back',
  doneBtnText: 'Done ✓',
};

/**
 * useTour Hook
 * 
 * Provides tour management functionality including:
 * - Starting tours
 * - Checking tour completion
 * - Re-triggering tours
 */
export const useTour = () => {
  const driverRef = useRef<Driver | null>(null);
  
  /**
   * Initialize driver instance
   */
  const getDriver = useCallback(() => {
    if (!driverRef.current) {
      driverRef.current = driver(driverConfig);
    }
    return driverRef.current;
  }, []);
  
  /**
   * Start a specific tour
   */
  const startTour = useCallback((tourType: TourType, forceRestart = false) => {
    const tourKey = `${tourType}_completed` as keyof TourState;
    
    // Check if tour was already completed (unless forcing restart)
    if (!forceRestart && isTourCompleted(tourKey)) {
      console.log(`[Tour] ${tourType} already completed, skipping`);
      return;
    }
    
    const config = getTourConfig(tourType);
    
    if (config.steps.length === 0) {
      console.log(`[Tour] No steps defined for ${tourType}`);
      return;
    }
    
    // Filter out steps where element doesn't exist
    const availableSteps = config.steps.filter(step => {
      if (!step.element) return true; // Highlight steps without element
      const el = document.querySelector(step.element as string);
      return el !== null;
    });
    
    if (availableSteps.length === 0) {
      console.log(`[Tour] No valid elements found for ${tourType} tour`);
      return;
    }
    
    const driverInstance = getDriver();
    
    driverInstance.setConfig({
      ...driverConfig,
      steps: availableSteps,
      onDestroyStarted: () => {
        // Called when user clicks overlay or close button
        config.onSkip?.();
        driverInstance.destroy();
      },
      onDestroyed: () => {
        // Called after tour is fully destroyed
        config.onComplete?.();
      },
    });
    
    console.log(`[Tour] Starting ${tourType} tour with ${availableSteps.length} steps`);
    driverInstance.drive();
  }, [getDriver]);
  
  /**
   * Stop the current tour
   */
  const stopTour = useCallback(() => {
    if (driverRef.current) {
      driverRef.current.destroy();
    }
  }, []);
  
  /**
   * Check if a tour should auto-start
   */
  const shouldAutoStart = useCallback((tourType: TourType): boolean => {
    const tourKey = `${tourType}_completed` as keyof TourState;
    return !isTourCompleted(tourKey);
  }, []);
  
  /**
   * Replay a tour (force restart even if completed)
   */
  const replayTour = useCallback((tourType: TourType) => {
    startTour(tourType, true);
  }, [startTour]);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (driverRef.current) {
        driverRef.current.destroy();
      }
    };
  }, []);
  
  return {
    startTour,
    stopTour,
    replayTour,
    shouldAutoStart,
    isTourCompleted,
    resetTour,
    resetAllTours,
  };
};

export default useTour;
